"use client";

import { Accordion as AccordionPrimitive } from "@base-ui/react/accordion";
import { ChevronDownIcon } from "@radix-ui/react-icons";
import * as React from "react";

import { cn } from "@/utils/lib";
import { resolveClassName } from "@/utils/resolve-class-name";

type AccordionProps = Omit<
  React.ComponentProps<typeof AccordionPrimitive.Root<string>>,
  "defaultValue" | "multiple" | "onValueChange" | "value"
> & {
  collapsible?: boolean;
  defaultValue?: string | string[];
  onValueChange?: (value: string | string[]) => void;
  type?: "multiple" | "single";
  value?: string | string[];
};

const Accordion = ({
  className,
  collapsible,
  defaultValue,
  onValueChange,
  type = "multiple",
  value,
  ...props
}: AccordionProps) => {
  void collapsible;
  const multiple = type === "multiple";

  const normalizeValue = React.useCallback((input?: string | string[]) => {
    if (input === undefined) {
      return undefined;
    }

    if (Array.isArray(input)) {
      return input;
    }

    return input ? [input] : [];
  }, []);

  return (
    <AccordionPrimitive.Root
      className={(state) =>
        cn("flex w-full flex-col", resolveClassName(className, state))
      }
      defaultValue={normalizeValue(defaultValue)}
      multiple={multiple}
      onValueChange={(nextValue) => {
        onValueChange?.(multiple ? [...nextValue] : (nextValue[0] ?? ""));
      }}
      value={normalizeValue(value)}
      {...props}
    />
  );
};

const AccordionItem = ({
  className,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Item>) => (
  <AccordionPrimitive.Item
    className={(state) =>
      cn("border-b border-border-light", resolveClassName(className, state))
    }
    {...props}
  />
);

const AccordionTrigger = ({
  className,
  children,
  nativeButton = false,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Trigger>) => (
  <AccordionPrimitive.Header className="flex">
    <AccordionPrimitive.Trigger
      nativeButton={nativeButton}
      className={(state) =>
        cn(
          `
            group flex flex-1 items-center justify-between py-4 text-sm
            font-medium transition-all
            hover:underline hover:underline-offset-4
            aria-disabled:pointer-events-none aria-disabled:opacity-50
          `,
          resolveClassName(className, state),
        )
      }
      {...props}
    >
      {children}
      <ChevronDownIcon
        className="
          size-4 shrink-0 text-muted-foreground transition-transform
          duration-200
          group-aria-expanded:rotate-180
        "
      />
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
);

const AccordionContent = ({
  className,
  children,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Panel>) => (
  <AccordionPrimitive.Panel className="overflow-hidden" {...props}>
    <div
      className={cn(
        `
          h-(--accordion-panel-height) pt-0 pb-4 text-sm
          transition-[height]
          data-ending-style:h-0
          data-starting-style:h-0
        `,
        className,
      )}
    >
      {children}
    </div>
  </AccordionPrimitive.Panel>
);

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger };
